<template>
    <div>

        <div class="subscription-message-container">
            <!-- Main Message Card -->
            <div v-if="!canAccessPortal"
                 class="message-card">
                <div>
                    <h3 class="title">Only group admin has right to manage subscription</h3>
                </div>
            </div>

            <div v-if="canAccessPortal"
                 class="message-card">
                <div class="icon-section">
                    <svg class="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>

                <div class="content-section">
                    <h3 class="title">{{$t('subscriptionPage.manageSubscription')}}</h3>
                    <p class="message">
                        {{$t('subscriptionPage.subscriptionIsManagedInStripe')}}
                        {{$t('subscriptionPage.accessToCustomerPortal')}}
                    </p>

                    <ul class="features-list">
                        <li>
                            {{$t('subscriptionPage.updatePaymentMethod')}}
                        </li>
                        <li>
                            {{$t('subscriptionPage.changeSubscripntionPlan')}}
                        </li>
                        <li>
                            {{$t('subscriptionPage.cancelYourSubscription')}}
                        </li>
                    </ul>

                    <button @click="openPortal"
                            :disabled="loading"
                            class="portal-button">
                        <span v-if="!loading">{{$t('subscriptionPage.openSubscriptionPortal')}}</span>
                        <span v-else class="loading-text">
                            <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="40" stroke-dashoffset="10">
                                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                                </circle>
                            </svg>           
                            {{$t('subscriptionPage.openingPortal')}}

                        </span>
                    </button>

                    <p class="security-note">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L3.09 5.26C2.39 5.52 2 6.21 2 6.95V11C2 16.55 5.84 21.74 12 23C18.16 21.74 22 16.55 22 11V6.95C22 6.21 21.61 5.52 20.91 5.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                            <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>

                        {{$t('subscriptionPage.youWillBeRedirected')}}
                    </p>
                </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="error-alert">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
                    <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <span>{{ error }}</span>
                <button @click="error = null" class="close-button">&times;</button>
            </div>
        </div>

    </div>
</template>

<script lang="js">
    import { defineComponent } from 'vue';
    import { useUserStore } from '@/stores/userStore';

    export default defineComponent({
        data() {
            return {
                userStore: useUserStore(),
                canAccessPortal: false,
                loading: false,
                error: null,
            }
        },
        created() {
            this.init();
        },
        methods: {
            async init() {
                this.canAccessPortal = this.userStore.isGroupAdmin;
            },
            async openPortal() {
                if (!this.canAccessPortal) {
                    this.error = 'Please ensure your account is set up for billing'
                    return
                }

                this.loading = true
                this.error = null

                try {
                    const response = await this.userStore.getUserSubscription();
                    window.location.href = response.data
                } catch (error) {
                    const message = error.response?.data?.error || 'Failed to open billing portal'
                    this.error = message
                    console.error('Customer portal error:', error)
                } finally {
                    this.loading = false
                }
            }
        }
    });
</script>

<style scoped>
    .subscription-message-container {
        margin: 0 auto;
    }

    .message-card {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        padding: 32px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .icon-section {
        display: flex;
        justify-content: center;
        margin-bottom: 24px;
    }

    .icon {
        width: 48px;
        height: 48px;
        color: #4f46e5;
    }

    .content-section {
        text-align: center;
    }

    .title {
        font-size: 24px;
        font-weight: 600;
        color: #111827;
        margin: 0 0 16px;
    }

    .message {
        font-size: 16px;
        color: #4b5563;
        line-height: 1.6;
        margin: 0 0 20px;
    }

    .features-list {
        list-style: none;
        padding: 0;
        margin: 0 0 32px;
        text-align: left;
        max-width: 300px;
        margin-left: auto;
        margin-right: auto;
    }

        .features-list li {
            position: relative;
            padding-left: 28px;
            margin-bottom: 12px;
            color: #374151;
            font-size: 15px;
        }

            .features-list li::before {
                content: "✓";
                position: absolute;
                left: 0;
                color: #059669;
                font-weight: bold;
            }

    .portal-button {
        background: #4f46e5;
        color: white;
        border: none;
        padding: 12px 32px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

        .portal-button:hover:not(:disabled) {
            background: #4338ca;
            transform: translateY(-1px);
            box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.3);
        }

        .portal-button:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }

    .loading-text {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .spinner {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .security-note {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-top: 16px;
        font-size: 14px;
        color: #64748b;
    }

        .security-note svg {
            color: #059669;
        }

    .error-alert {
        background: #fee2e2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 12px 16px;
        margin-top: 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        color: #b91c1c;
        font-size: 14px;
    }

        .error-alert svg {
            flex-shrink: 0;
        }

    .close-button {
        margin-left: auto;
        background: none;
        border: none;
        font-size: 24px;
        color: #dc2626;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s;
    }

    .close-button:hover {
        background: rgba(220, 38, 38, 0.1);
    }

</style>
